import {FC, useEffect, useState} from 'react'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import {DateTimeField} from './common/DateTimeField'
import {FdsSelect} from './common/FdsSelect'
import {addDays, format, lastDayOfWeek} from 'date-fns'
import {LaborTemplateListItemModel, WorkflowActionModel} from '../core/_models'

/**
 * The Options-menu actions that need an input before they can run.
 *
 * Each used to be a two-step trip through the menu itself — pick the action in
 * a select, fill a field that appeared under it, press the menu's Apply, then
 * confirm in a modal. Now the menu item opens this dialog straight away: the
 * input and the confirmation are one step, and the primary button stays
 * disabled until the input is valid, so there is no "required" toast to miss.
 */
export type ActionForm =
  | {kind: 'reason'; action: WorkflowActionModel}
  | {kind: 'copy_from'}
  | {kind: 'apply_template'}
  | {kind: 'upload_file'}

type GuardList = WorkflowActionModel['guards']

const GuardResults: FC<{guards: GuardList}> = ({guards}) => (
  <ul className='list-unstyled mb-3'>
    {guards.map((g) => (
      <li key={g.name} className='text-gray-600 fs-6 mb-1'>
        {g.ok ? (
          <i className='fa fa-check-circle text-success me-2'></i>
        ) : g.severity === 'block' ? (
          <i className='fa fa-times-circle text-danger me-2'></i>
        ) : (
          <i className='fa fa-exclamation-triangle text-warning me-2'></i>
        )}
        {g.message || g.label || g.name}
      </li>
    ))}
  </ul>
)

type Props = {
  form: ActionForm | null
  onClose: () => void
  selectedDate: Date
  weekStartOn: 0 | 1 | 2 | 3 | 4 | 5 | 6
  weekStart: string | undefined
  templates: LaborTemplateListItemModel[] | null
  onReason: (action: WorkflowActionModel, reason: string, acknowledged: string[]) => void
  onCopyFrom: (date: Date) => void
  onApplyTemplate: (template: LaborTemplateListItemModel) => void
  onUpload: (file: File) => void
}

const SchedulerActionForm: FC<Props> = ({
  form,
  onClose,
  selectedDate,
  weekStartOn,
  weekStart,
  templates,
  onReason,
  onCopyFrom,
  onApplyTemplate,
  onUpload,
}) => {
  const [reason, setReason] = useState('')
  const [copyFromDate, setCopyFromDate] = useState<Date>(addDays(selectedDate, -7))
  const [templateUuid, setTemplateUuid] = useState('')
  const [file, setFile] = useState<File | null>(null)

  // Fresh inputs every time a dialog opens — a reason typed for "Reject" must
  // not resurface under "Approve".
  useEffect(() => {
    if (!form) return
    setReason('')
    setCopyFromDate(addDays(selectedDate, -7))
    setTemplateUuid('')
    setFile(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form])

  if (!form) return null

  const template = templates?.find((t) => t.uuid === templateUuid)
  const failingWarnings =
    form.kind === 'reason' ? form.action.guards.filter((g) => g.severity === 'warn' && !g.ok) : []

  let title = ''
  let submitText = ''
  let submitVariant = 'primary'
  let canSubmit = false
  let body: JSX.Element | null = null
  let submit = () => {}

  if (form.kind === 'reason') {
    const {action} = form
    title = action.label
    submitText = failingWarnings.length ? 'Proceed anyway' : action.label
    canSubmit = reason.trim().length > 0
    submit = () =>
      onReason(
        action,
        reason.trim(),
        failingWarnings.map((g) => g.name)
      )
    body = (
      <>
        {action.guards.length > 0 && (
          <>
            <div className='text-gray-700 fs-7 fw-bold mb-2'>Checks:</div>
            <GuardResults guards={action.guards} />
          </>
        )}
        <label className='form-label fw-bold' htmlFor='workflow-reason'>
          Reason
        </label>
        <textarea
          id='workflow-reason'
          className='form-control'
          rows={3}
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={`Why "${action.label}"?`}
        />
        <div className='text-gray-600 fs-7 mt-1'>Required — it is saved in the history.</div>
      </>
    )
  } else if (form.kind === 'copy_from') {
    title = 'Copy from a previous week'
    submitText = 'Copy and replace'
    submitVariant = 'danger'
    canSubmit = true
    submit = () => onCopyFrom(copyFromDate)
    body = (
      <>
        <DateTimeField
          id='copy-from-week'
          label='Week to copy'
          pick='week'
          value={copyFromDate}
          onChange={setCopyFromDate}
          weekStartOn={weekStartOn}
          maxDate={addDays(lastDayOfWeek(selectedDate, {weekStartsOn: weekStartOn}), -7)}
        />
        <div className='text-danger fs-7 fw-bold mt-3'>
          This week's current shifts are removed first. This cannot be undone.
        </div>
      </>
    )
  } else if (form.kind === 'apply_template') {
    title = 'Apply schedule template'
    submitText = 'Apply template'
    canSubmit = !!template
    submit = () => template && onApplyTemplate(template)
    body = (
      <>
        <label className='form-label fw-bold' htmlFor='apply-template'>
          Template
        </label>
        {templates === null ? (
          <div className='text-gray-600 fs-7'>Loading templates…</div>
        ) : templates.length === 0 ? (
          <div className='text-gray-600 fs-7'>
            No templates saved for this store yet — build one with the Templates switch in the
            toolbar.
          </div>
        ) : (
          <FdsSelect<string>
            id='apply-template'
            width='100%'
            placeholder='Select a template'
            value={templateUuid || null}
            onChange={setTemplateUuid}
            options={templates.map((t) => ({
              value: t.uuid,
              label: `${t.name} (${
                t.template_type === 'daily'
                  ? `daily — applies to ${format(selectedDate, 'MMM d')}`
                  : `weekly — applies to week of ${weekStart}`
              })`,
            }))}
          />
        )}
        <div className='text-gray-600 fs-7 mt-3'>
          The template's shifts are added as unassigned shifts — you pick the employees afterwards.
        </div>
      </>
    )
  } else if (form.kind === 'upload_file') {
    title = 'Upload schedule from file'
    submitText = 'Upload and replace'
    submitVariant = 'danger'
    canSubmit = !!file
    submit = () => file && onUpload(file)
    body = (
      <>
        <input
          type='file'
          className='form-control'
          onChange={(e) => setFile((e.target.files && e.target.files[0]) || null)}
        />
        <div className='text-danger fs-7 fw-bold mt-3'>
          This week's current shifts are removed first. This cannot be undone.
        </div>
      </>
    )
  }

  return (
    <Modal show onHide={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (!canSubmit) return
          onClose()
          submit()
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{body}</Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={onClose}>
            Cancel
          </Button>
          <Button type='submit' variant={submitVariant} disabled={!canSubmit}>
            {submitText}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

export {SchedulerActionForm}
